import {TypedEventTarget} from '../../logics/TypedEventTarget';
import {
  ETextParamType_OnReceivedStartScreenShare,
  ETextParamType_OnReceivedStopScreenShare,
  ETextParamType_OnStopScreenShare,
  HEL_AUDIO_TYPE_VOICE
} from '../../logics/const';
import {
  getMixerMuteAt,
  getMixerSettingAt,
  getSkywayOnTellScreenShareStart,
  getSkywayOnTellWaitScreenShare,
  getUseSkywayAudioGainToggle
} from '../../logics/dataOps';
import {closeLocalStream, getLocalStream, setLocalStream} from '../../logics/localStream';
import {hel_setText} from '../../logics/moduleCaller';
import {hel_isiPhone} from '../../logics/platform';
import {setMicEnabled} from '../../logics/shared';
import {
  hel_video_stop,
  hel_video_close_genericwindow,
  hel_skyway_stop_screen_sharing_via_room,
  hel_video_is_playing_same_stream
} from '../../logics/video_callee';
import {GetIHeliNetwork} from '../../singleton_registry';
import AgoraClient, {ensureAgoraLibraryLoadedAndInitialized} from '../AgoraClient';
import {
  onNetReceiveVoiceJoin,
  onNetReceiveVoiceLeave,
  onNetVoiceConnected,
  onNetVoiceDisconnected
} from '../networkCallback';

import type {
  IHeliVoiceChannelProvider,
  VoiceChannelProviderEventMap
} from './IHeliVoiceChannelProvider';
import type {PositionChannelDetail} from '../../types/network';
import type {IHeliMic, IHeliRtcTokenRoomTarget, Transform} from '../CHeliNetwork';
import type {CHeliPlayer} from '../CHeliPlayer';
import type {PositionChannelProviderEventMap} from '../positionChannelProviders/IHeliPositionChannelProvider';
import type {IAgoraRTCRemoteUser} from 'agora-rtc-sdk-ng';

/**
 * ブラウザコンソールからのデバッグのために、グローバル変数へ保存する。
 * @see https://discord.com/channels/596169759045976094/1150648243387125790/1326351292033138771
 */
const registerAgoraClientAsGlobalVariableForDebug = (agoraClient: AgoraClient) => {
  Object.assign(globalThis, {agoraClient});
};

export class CHeliAgoraVoiceChannelProvider
  extends TypedEventTarget<VoiceChannelProviderEventMap>
  implements IHeliVoiceChannelProvider
{
  #agoraClient: AgoraClient | null = null;

  /** プレイヤーごとの、画面共有の映像において必要となるデータ。 */
  readonly #playerIdScreenShareVideoDataPairs = new Map<
    string,
    {mediaStream: MediaStream; itemName: string; materialName: string}
  >();

  readonly #playerIdAudioPairs: Map<
    string,
    {
      agoraUser: IAgoraRTCRemoteUser | null;
      attenuateVolume: number;
    }
  > = new Map();

  #globalPlayerVoiceVolume = getMixerSettingAt(HEL_AUDIO_TYPE_VOICE);

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    await ensureAgoraLibraryLoadedAndInitialized();
  }

  prepareForNewJoin(): boolean {
    return true;
  }

  onRemoteJoin(): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    // 画面共有開始通知
    heliNetwork.sendScreenShareStart();
  }

  onRemoteLeave(detail: PositionChannelProviderEventMap['remoteleave']['detail']): void {}

  #getVideoData(playerId: string): {
    mediaStream: MediaStream;
    itemName: string;
    materialName: string;
  } {
    const videoData = this.#playerIdScreenShareVideoDataPairs.get(playerId);
    if (videoData) {
      return videoData;
    }

    const newVideoData = {
      mediaStream: new MediaStream(),
      itemName: '',
      materialName: ''
    };
    this.#playerIdScreenShareVideoDataPairs.set(playerId, newVideoData);
    return newVideoData;
  }

  connectVoiceChannel(): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const client = new AgoraClient({
      mode: 'rtc',
      ctrlRemoteVideoLiveStreamVolume: hel_isiPhone()
    });
    client.on('subscribePlayerAudio', ({user}) => {
      console.debug('subscribePlayerAudio user:', user.hasAudio);
      if (user.audioTrack) {
        const player = heliNetwork.findPlayerByRtcUserCode(
          typeof user.uid === 'number' ? user.uid : Number.parseInt(user.uid)
        );
        if (!player) {
          console.error('no player agoraUser.uid', user.uid);
          return;
        }
        this.#applyPlayerVolume(player);
        user.audioTrack.play();
      }
    });

    // 画面共有配信終了時
    client.on('stopRemoteVideoLiveStream', ({user, remoteVideoLiveStream}) => {
      console.debug('screen share streaming stopped', user, remoteVideoLiveStream);
    });
    // 画面共有配信受信時(start)
    client.on('startRemoteVideoLiveStream', ({user, remoteVideoLiveStream}) => {
      console.debug('screen share streaming start', user, remoteVideoLiveStream);
      const player = heliNetwork.findPlayerByRtcUserCode(
        typeof user.uid === 'number' ? user.uid : Number.parseInt(user.uid)
      );
      if (!player) {
        console.error('no player agoraUser.uid', user.uid);
        return;
      }

      this.#getVideoData(player.getPlayerId()).mediaStream = remoteVideoLiveStream;
      this.#playScreenShareByPlayer(player);
    });

    window.addEventListener('beforeunload', () => {
      hel_skyway_stop_screen_sharing_via_room();
      closeLocalStream();
      client.close();
    });
    // 他プレーヤーjoin自
    client.onUserJoined((agoraUser) => {
      console.debug('agoraClient onUserJoined', agoraUser);
      const player = heliNetwork.findPlayerByRtcUserCode(
        typeof agoraUser.uid === 'number' ? agoraUser.uid : Number.parseInt(agoraUser.uid)
      );
      if (!player) {
        console.error('no player agoraUser.uid', agoraUser.uid);
        return;
      }
      this.#getPlayerAudio(player).agoraUser = agoraUser;

      // 音声チャンネルへのJoinを通知
      onNetReceiveVoiceJoin(player.getPlayerId(), '');
    });
    client.onUserLeft((agoraUser) => {
      console.debug('agoraClient onUserLeft', agoraUser);
      const player = heliNetwork.findPlayerByRtcUserCode(
        typeof agoraUser.uid === 'number' ? agoraUser.uid : Number.parseInt(agoraUser.uid)
      );
      if (!player) {
        console.error('no player agoraUser.uid', agoraUser.uid);
        return;
      }

      // 音声チャンネルからのLeaveを通知
      onNetReceiveVoiceLeave(player.getPlayerId(), '');
    });

    // 自分がチャンネル接続時
    client.onConnected((localUser) => {
      onNetVoiceConnected(
        typeof localUser.uid === 'number' ? localUser.uid.toString() : localUser.uid
      );
    });
    client.onDisconnected(() => {
      onNetVoiceDisconnected();
    });
    this.#agoraClient = client;
    registerAgoraClientAsGlobalVariableForDebug(client);
  }

  #playScreenShareByPlayer(player: CHeliPlayer) {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const videoData = this.#getVideoData(player.getPlayerId());
    const remoteVideoStream = videoData.mediaStream;
    if (hel_video_is_playing_same_stream(remoteVideoStream)) {
      return;
    }
    // グローバルにhel_onReceiveNoticeScreenShareStartが定義されていた場合に実行する
    if (typeof globalThis.hel_onReceiveNoticeScreenShareStart === 'function') {
      globalThis.hel_onReceiveNoticeScreenShareStart(
        player.getPlayerId(),
        remoteVideoStream,
        videoData.itemName,
        videoData.materialName
      );
    }

    // 予約
    heliNetwork.reserveRemotoVideoStream(
      remoteVideoStream,
      videoData.itemName,
      videoData.materialName
    );

    // HeliScriptのコールバックを呼び出す
    hel_setText(ETextParamType_OnReceivedStartScreenShare, '');
  }

  onData(data: string, playerId: string): void {
    if (data === 'screenshare:tell') {
      const fn = getSkywayOnTellScreenShareStart();
      if (fn !== undefined) {
        fn();
        return;
      }
    }
    if (data === 'screenshare:waitstart') {
      const fn = getSkywayOnTellWaitScreenShare();
      if (fn !== undefined) {
        fn();
        return;
      }
    }
    const paramsScreenShare = data.split(':');
    if (paramsScreenShare[1] === 'start') {
      const videoData = this.#getVideoData(playerId);
      const paramsScreenShare = data.split(':');
      if (paramsScreenShare[2] === undefined || paramsScreenShare[3] === undefined) {
        throw new Error(
          `Invalid screenshare:start format. Expected “screenshare:start:<videoItemName>:<videoItemMaterial>”, but got “${data}”.`
        );
      }
      videoData.itemName = paramsScreenShare[2];
      videoData.materialName = paramsScreenShare[3];
    }
    if (paramsScreenShare[1] === 'stop') {
      // グローバルにhel_onReceiveNoticeScreenShareStopが定義されていた場合に実行する
      if (typeof globalThis.hel_onReceiveNoticeScreenShareStop === 'function') {
        globalThis.hel_onReceiveNoticeScreenShareStop(playerId);
      } else {
        hel_video_stop();

        hel_video_close_genericwindow();

        // HeliScriptのコールバックを呼び出す
        hel_setText(ETextParamType_OnReceivedStopScreenShare, '');
      }
    }
  }

  disconnectFromVoiceChannel(): void {}

  async prepareLeavePositionChannel(): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const positionChannel = heliNetwork.getCurrentPositionChannel();
    if (positionChannel && (!positionChannel.isMultiVc() || positionChannel.isEnableDefaultVc())) {
      heliNetwork.micReady = false;
      heliNetwork.liveMicReady = false;
      await this.#leaveVoiceRtc();

      if (positionChannel.isEnableDefaultVc()) {
        heliNetwork.setCurrentVoiceChannel(null);
        // 音声チャンネルからの退出を通知
        onNetVoiceDisconnected();
      }
    }
  }

  async postLeaveVoiceChannelCleanup(): Promise<void> {
    await this.#leaveVoiceRtc();
  }

  #leaveVoiceRtc() {
    if (this.#agoraClient) {
      hel_skyway_stop_screen_sharing_via_room();
      this.#agoraClient.close();
    }
  }

  async createGenericRtcTokenAndJoinVoiceChannel(
    pIHeliMic: IHeliMic | null,
    pIHeliRtcTokenRoomTarget: IHeliRtcTokenRoomTarget | null
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();

    // 座標チャンネルに接続済みか判定
    if (!currentPosCh) {
      throw new Error('getCurrentPositionChannel() returns null.');
    }

    // 既に接続済みの場合は一旦切断
    await this.#leaveVoiceRtc();

    if (!heliNetwork.micReady) {
      await heliNetwork.initLocalMicInput(pIHeliMic?.deviceId ?? null);
    }
    const channelSessionJwt = (
      await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
    ).data;
    if (!channelSessionJwt) {
      throw new Error('channelSessionApi.getChannelSessionJwt() returns null');
    }

    const type = 'agora';
    const mode = 'talk';
    const disableRtcUser = false;
    const returnValue = await globalThis.heliport.v3.api.genericRtcToken.getToken({
      channelSessionJwt,
      type,
      mode,
      disableRtcUser,
      roomTarget: pIHeliRtcTokenRoomTarget ?? undefined
    });
    if (returnValue.status !== 'ok') {
      throw new Error(`genericRtcToken.getToken() failed: ${JSON.stringify(returnValue, null, 2)}`);
    }
    const token = returnValue.data;
    const agoraToken = token.token;
    const agoraAppId = token.app_id;
    const agoraChannelId = token.room_id;
    if (!agoraChannelId) {
      throw new Error('The room_id returned by genericRtcToken.getToken() is undefined.');
    }

    // TODO agoraUserID と CRP objectIDの変換テーブル
    const agoraUserId = isNaN(Number(token.user_id)) ? token.user_id : Number(token.user_id);

    const micEnabled = false;
    if (!this.#agoraClient) {
      throw new Error('agoraClient is not defined');
    }
    await this.#agoraClient.joinTalk(
      agoraAppId,
      agoraToken,
      agoraChannelId,
      agoraUserId,
      micEnabled
    );
    await heliNetwork.checkMicPermissionState();
  }

  async onJoinPositionChannel(
    positionChannelDetail: PositionChannelDetail,
    channelSessionJwt: string
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();

    // 座標チャンネルに接続済みか判定
    if (!currentPosCh) {
      throw new Error('getCurrentPositionChannel() returns null.');
    }

    if (currentPosCh.isMultiVc() && currentPosCh.isEnableDefaultVc()) {
      const pIHeliMic = await heliNetwork.getMicDevice();
      await this.createGenericRtcTokenAndJoinVoiceChannel(pIHeliMic, 'position_channel_code');
    }
  }

  initializePlayerVoice(player: CHeliPlayer): void {
    this.#playerIdAudioPairs.set(player.getPlayerId(), {
      agoraUser: null,
      attenuateVolume: 0.0
    });
    this.#applyPlayerVolume(player);
  }
  #getPlayerAudio(player: CHeliPlayer) {
    const playerId = player.getPlayerId();
    const audio = this.#playerIdAudioPairs.get(playerId);
    if (!audio) {
      throw new Error(
        `initializePlayerVoice() must be called for playerId “${playerId}” before calling #getPlayerAudio().`
      );
    }
    return audio;
  }
  onPlayerRemoved(player: CHeliPlayer): void {
    this.#playerIdScreenShareVideoDataPairs.delete(player.getPlayerId());
  }
  setPlayerVoiceStream(playerOrPlayerId: CHeliPlayer | string, stream: MediaStream): void {}
  #applyPlayerVolume(player: CHeliPlayer): void {
    // 距離減衰有効無効
    if (!getUseSkywayAudioGainToggle()) {
      return;
    }
    const audio = this.#getPlayerAudio(player);
    if (audio.agoraUser && audio.agoraUser.hasAudio && audio.agoraUser.audioTrack) {
      // player個人のミュートもしくは自身の設定からミュートの場合はvolumeを0にする
      const isMuted = getMixerMuteAt(HEL_AUDIO_TYPE_VOICE);
      const mute = player.mute || isMuted ? true : false;
      const fixedVolume =
        this.#globalPlayerVoiceVolume * audio.attenuateVolume * (mute ? 0.0 : 1.0);
      audio.agoraUser.audioTrack.setVolume(100.0 * fixedVolume);
    }
  }
  applyPlayerVoiceMute(player: CHeliPlayer): void {
    this.#applyPlayerVolume(player);
  }
  setPlayerVoiceAttenuate(player: CHeliPlayer, volume: number): void {
    this.#getPlayerAudio(player).attenuateVolume = volume;
    this.#applyPlayerVolume(player);
  }
  setGlobalPlayerVoiceVolume(volume: number): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    this.#globalPlayerVoiceVolume = volume;
    for (const player of heliNetwork.getPlayerList().values()) {
      this.#applyPlayerVolume(player);
    }
  }

  async startScreenShareStream(useCamera: boolean, useAudio: boolean): Promise<MediaStream | null> {
    if (!this.#agoraClient) {
      throw new Error('agoraClient is not defined');
    }
    // 未接続の場合はreturn
    if (!this.#agoraClient.isConnected()) {
      console.error('agora client join yet');
      return null;
    }

    const localVideoStream = await this.#getLocalVideoStream(useCamera, useAudio);
    if (!localVideoStream) {
      throw new Error('CHeliCarnelianAgora.getLocalVideoStream() returns undefined');
    }
    await this.#agoraClient.publishVideoStream(localVideoStream);

    return localVideoStream;
  }
  async #getLocalVideoStream(useCamera: boolean, useAudio: boolean) {
    if (useCamera) {
      const localVideoStream = await window.navigator.mediaDevices
        .getUserMedia({
          audio: useAudio,
          video: true
        })
        .catch(console.error);
      if (!localVideoStream) {
        console.error('');
        return;
      }
      return localVideoStream;
    } else {
      const localVideoStream = await window.navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always'
        },
        audio: false
      });
      const track = localVideoStream.getTracks()[0];
      if (!track) {
        throw new Error(
          '(await navigator.mediaDevices.getDisplayMedia()).getTracks() returns an empty array.'
        );
      }
      track.addEventListener('ended', () => {
        console.debug('screen share ended');
        hel_skyway_stop_screen_sharing_via_room();
      });
      return localVideoStream;
    }
  }
  async cleanupScreenShareStream(): Promise<boolean> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    if (this.#agoraClient && this.#agoraClient._localAgoraTracks['videoTrack']) {
      // 画面共有を行っている側の処理
      await this.#agoraClient.unpublishVideoStream();

      heliNetwork.sendData(`screenshare:stop`);

      // HUD上のミニビューアー表示を閉じる
      hel_video_close_genericwindow();

      // HeliScriptのコールバックを呼び出す
      hel_setText(ETextParamType_OnStopScreenShare, '');

      return true;
    } else {
      hel_video_close_genericwindow();

      // HeliScriptのコールバックを呼び出す
      hel_setText(ETextParamType_OnReceivedStopScreenShare, '');

      return false;
    }
  }
  onPlayScreenShare(): void {}
  useNotEmptyLocalStream(): boolean {
    return getLocalStream() ? true : false;
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    if (!this.#agoraClient) {
      throw new Error('agoraClient is not defined');
    }
    this.#agoraClient.setMicMuted(!enabled);
  }
  getMicEnabled(): boolean {
    if (!this.#agoraClient) {
      return false;
    }
    if (this.#agoraClient.isMicMuted()) {
      return false;
    }
    return !this.#agoraClient.isMicMuted();
  }
  supportsSpeakerMode(): boolean {
    return false;
  }

  setCurrentLocalTransform(param: {player: Transform; camera: Transform}): void {}

  async joinVoiceRtc(
    pIHeliMic: IHeliMic | null,
    pIHeliRtcTokenRoomTarget: IHeliRtcTokenRoomTarget | null
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();

    // 座標チャンネルに接続済みか判定
    if (!currentPosCh) {
      throw new Error('getCurrentPositionChannel() returns null.');
    }

    await this.createGenericRtcTokenAndJoinVoiceChannel(pIHeliMic, pIHeliRtcTokenRoomTarget);

    console.debug('Agora joinVoiceRtc done');
  }
  #setLocalStreamFromAgoraLocalTrack() {
    if (!this.#agoraClient) {
      return false;
    }
    const localMediaStream = this.#agoraClient.getLocalMediaStream();
    setLocalStream(localMediaStream);
    return true;
  }

  async initLocalMicInput(deviceId: string | null): Promise<boolean> {
    if (!this.#agoraClient) {
      console.error('agoraClient is not defined');
      return false;
    }

    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    await heliNetwork.checkTrackState(deviceId);
    const localMediaStream = getLocalStream();
    if (!localMediaStream) {
      // マイク無許可時・マイクデバイスが一つも存在しないとき
      console.debug('getLocalStream() returns undefined.');
      return false;
    }
    await this.#agoraClient.initLocalTrack({
      microphoneId: deviceId ?? undefined,
      audioMediaStream: localMediaStream
    });
    return true;
  }
  async changeMicDevice(deviceId: string): Promise<boolean> {
    if (this.#agoraClient) {
      await this.#agoraClient.changeInputDevices({
        microphoneId: deviceId
      });
      closeLocalStream();
      this.#setLocalStreamFromAgoraLocalTrack();
      if (this.getMicEnabled()) {
        // detectのmaxDomainが0になるため、止めてから再開する
        setMicEnabled(false);
        setMicEnabled(true);
      }
    }
    return true;
  }

  setSpeakerMode(speakerMode: boolean) {
    return false;
  }

  isConnected() {
    return this.#agoraClient?.isConnected() ?? false;
  }

  connectRoomAfterPeerOpen(): void {}
}
